import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { ArrowLeft, ArrowRight, Check, Linkedin, Facebook, Link2, Mail } from "lucide-react";
import { Container, Avatar, PostCard, PLACEHOLDER_PHOTO } from "../components/ui";
import { postsFor, postMapFor, POST_DATE, coverFor } from "../content/posts";
import { TRIAL_PATH } from "../config/entrypoints";

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// CMS article bodies are sanitized HTML (server-side, S5). Inject slugified ids
// into the <h2> headings so the Overview sidebar + scroll-spy keep working, and
// return the heading list for the TOC.
function processArticleHtml(html: string): { html: string; headings: { h: string; id: string }[] } {
  const headings: { h: string; id: string }[] = [];
  const out = html.replace(/<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/g, (_m, _attrs, inner: string) => {
    const text = inner.replace(/<[^>]+>/g, "").trim();
    const id = slugify(text);
    headings.push({ h: text, id });
    return `<h2 id="${id}" class="scroll-mt-24">${inner}</h2>`;
  });
  return { html: out, headings };
}

export default function BlogPost() {
  // Story 4.8: posts come from the path locale's catalog (en=international set,
  // nl=belgium set). The fallback post is locale-aware so `/en/blog` never lands
  // on a belgium-only slug that doesn't exist under `en`.
  const { t, locale } = useLocale();
  const POSTS = postsFor(locale);
  const POST_MAP = postMapFor(locale);
  const DEFAULT_SLUG = POSTS[0].slug;
  // Story 4.3: content selection is path-driven (`blog/:slug`). A legacy
  // `blog?slug=<slug>` URL has no path slug → client-301 to the path form,
  // keeping the active locale prefix. Direct hits with neither show the default post.
  const { lang, slug: pathSlug } = useParams();
  const [params] = useSearchParams();
  const legacySlug = params.get("slug");
  // Compute the redirect target but DON'T early-return here: the hooks below run
  // unconditionally so the hook count is stable across the legacy→path render
  // (both routes share this component). The <Navigate> is returned after the hooks.
  const redirectTo = !pathSlug && legacySlug ? `/${lang}/blog/${legacySlug}` : null;
  const slug = pathSlug ?? legacySlug ?? DEFAULT_SLUG;
  const post = POST_MAP[slug] || POST_MAP[DEFAULT_SLUG];

  // Reset scroll to the top whenever the reader opens a different article.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  // CMS posts carry sanitized `html`; baked in-repo posts use structured `body`.
  const article = useMemo(() => (post.html ? processArticleHtml(post.html) : null), [post]);
  const headings = useMemo(
    () =>
      article
        ? article.headings
        : (post.body ?? [])
            .filter((s) => s.h)
            .map((s) => ({ h: s.h as string, id: slugify(s.h as string) })),
    [article, post],
  );

  // Scroll-spy: highlight the Overview entry for the section currently in view.
  const [activeId, setActiveId] = useState("");
  useEffect(() => {
    if (!headings.length) return;
    setActiveId(headings[0].id);
    const els = headings.map((h) => document.getElementById(h.id)).filter((e): e is HTMLElement => !!e);
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        }
        const top = headings.find((h) => visible.has(h.id));
        if (top) setActiveId(top.id);
      },
      // Activation band near the top of the viewport (just below the sticky nav).
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);

  // Memoized so the scroll-spy `activeId` state (updated on every scroll tick)
  // doesn't re-walk the post list each render. `POSTS` is a stable per-locale
  // array reference, so it only recomputes when the locale or article changes.
  const { prev, next, related } = useMemo(() => {
    const idx = POSTS.findIndex((p) => p.slug === post.slug);
    return {
      prev: idx > 0 ? POSTS[idx - 1] : null,
      next: idx >= 0 && idx < POSTS.length - 1 ? POSTS[idx + 1] : null,
      related: [
        ...POSTS.filter((p) => p.slug !== post.slug && p.tag === post.tag),
        ...POSTS.filter((p) => p.slug !== post.slug && p.tag !== post.tag),
      ].slice(0, 3),
    };
  }, [POSTS, post]);

  // Legacy `?slug=` URL → client-301 to the path form (after all hooks ran).
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <>
      {/* Hero */}
      <section className="bg-white">
        <Container className="pb-10 pt-14 md:pt-20">
          <div className="mx-auto flex max-w-[640px] flex-col items-center text-center">
            <LocaleLink to="/resources" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-faint hover:text-ink">
              <ArrowLeft size={15} strokeWidth={2} /> {t("blog:backToAll")}
            </LocaleLink>
            <h1 className="mt-8 font-display text-[32px] font-semibold leading-[1.12] tracking-tight text-ink md:text-[40px] md:leading-[48px]">
              {post.title}
            </h1>
            <p className="mt-5 max-w-[544px] text-[16px] leading-relaxed text-ink/50">{post.lead}</p>

            <div className="mt-8 flex w-full items-center justify-between gap-6 border-t border-line pt-6 text-left">
              <div className="flex items-center gap-2.5">
                <Avatar size={44} />
                <div className="flex flex-col">
                  <span className="text-[12px] leading-tight text-ink-faint">{t("blog:writtenBy")}</span>
                  <span className="text-[14px] font-medium leading-tight text-ink">Done-it</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-[12px] leading-tight text-ink-faint">{t("blog:publishedOn")}</span>
                <span className="text-[14px] font-medium leading-tight text-ink">{POST_DATE[post.slug]}</span>
              </div>
              {post.read && (
                <div className="flex flex-col">
                  <span className="text-[12px] leading-tight text-ink-faint">{t("blog:readLabel")}</span>
                  <span className="text-[14px] font-medium leading-tight text-ink">{post.read}</span>
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* Cover */}
      <section className="bg-white">
        <Container>
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl border border-line bg-bg-subtle">
            <img src={coverFor(post)} alt={t("blog:coverAlt", { tag: post.tag })} className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </Container>
      </section>

      {/* Body + sidebar */}
      <section className="bg-white">
        <Container className="py-16 md:py-20">
          <div className="mx-auto flex max-w-[960px] flex-col gap-12 lg:flex-row lg:items-start lg:gap-[42px]">
            {/* Main column */}
            <div className="flex w-full flex-col gap-12 lg:max-w-[600px]">
              <article>
                {article ? (
                  // CMS rich HTML (sanitized server-side). Styled with the
                  // typography plugin, tuned to the design's ink tokens.
                  <div
                    className="prose prose-lg max-w-none font-ds prose-headings:font-display prose-headings:tracking-tight prose-headings:text-ink prose-h2:scroll-mt-24 prose-h2:text-[24px] prose-p:text-[16px] prose-p:leading-[1.7] prose-p:text-ink-soft prose-li:text-ink prose-a:text-primary prose-img:rounded-xl"
                    dangerouslySetInnerHTML={{ __html: article.html }}
                  />
                ) : (
                  (post.body ?? []).map((s, i) => (
                    <div key={i} className={i === 0 ? "" : s.h ? "mt-10" : "mt-5"}>
                      {s.h && (
                        <h2 id={slugify(s.h)} className="scroll-mt-24 font-display text-[22px] font-semibold tracking-tight text-ink md:text-[24px]">
                          {s.h}
                        </h2>
                      )}
                      {s.p && <p className={`text-[16px] leading-[1.7] text-ink-soft ${s.h ? "mt-3" : ""}`}>{s.p}</p>}
                      {s.list && (
                        <ul className="mt-4 flex flex-col gap-2.5">
                          {s.list.map((l) => (
                            <li key={l} className="flex items-start gap-2.5 text-[16px] leading-snug text-ink">
                              <Check size={18} strokeWidth={2.4} className="mt-1 shrink-0 text-success" />
                              {l}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))
                )}
              </article>

              <div className="h-px w-full bg-line" />

              {/* Author bio */}
              <div className="flex flex-col items-center gap-4 text-center">
                <Avatar size={56} />
                <div>
                  <p className="font-display text-[16px] font-semibold text-ink">Done-it</p>
                  <p className="mx-auto mt-1 max-w-md text-[14px] leading-relaxed text-ink-soft">
                    {t("blog:authorBio")}
                  </p>
                </div>
                <LocaleLink to="/about" className="group inline-flex items-center gap-2.5">
                  <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-white transition-transform group-hover:translate-x-0.5">
                    <ArrowRight size={11} strokeWidth={2.4} />
                  </span>
                  <span className="text-[14px] font-semibold text-ink/60">{t("blog:moreAboutDoneit")}</span>
                </LocaleLink>
              </div>

              {(prev || next) && <div className="h-px w-full bg-line" />}

              {/* Prev / Next */}
              {(prev || next) && (
                <div className="flex flex-col gap-3">
                  {prev && (
                    <LocaleLink
                      to={`/blog/${prev.slug}`}
                      className="group flex items-center gap-3 rounded-xl border border-line bg-bg-subtle p-3 transition-colors hover:border-line-strong hover:bg-white"
                    >
                      <ArrowLeft size={18} strokeWidth={2} className="shrink-0 text-ink-faint transition-colors group-hover:text-primary" />
                      <span className="h-12 w-16 shrink-0 overflow-hidden rounded-md border border-line bg-bg-subtle">
                        <img src={coverFor(prev)} alt={t("blog:postThumbAlt", { tag: prev.tag, title: prev.title })} className="h-full w-full object-cover" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12px] text-ink-faint">{t("blog:previousPost")}</span>
                        <span className="block truncate text-[14px] font-semibold text-ink transition-colors group-hover:text-primary">
                          {prev.title}
                        </span>
                      </span>
                    </LocaleLink>
                  )}
                  {next && (
                    <LocaleLink
                      to={`/blog/${next.slug}`}
                      className="group flex items-center gap-3 rounded-xl border border-line bg-bg-subtle p-3 text-right transition-colors hover:border-line-strong hover:bg-white"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] text-ink-faint">{t("blog:nextPost")}</span>
                        <span className="block truncate text-[14px] font-semibold text-ink transition-colors group-hover:text-primary">
                          {next.title}
                        </span>
                      </span>
                      <span className="h-12 w-16 shrink-0 overflow-hidden rounded-md border border-line bg-bg-subtle">
                        <img src={coverFor(next)} alt={t("blog:postThumbAlt", { tag: next.tag, title: next.title })} className="h-full w-full object-cover" />
                      </span>
                      <ArrowRight size={18} strokeWidth={2} className="shrink-0 text-ink-faint transition-colors group-hover:text-primary" />
                    </LocaleLink>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="hidden shrink-0 lg:block lg:w-[300px] lg:self-start lg:sticky lg:top-24">
              <div className="flex flex-col gap-9">
                {headings.length > 0 && (
                  <div>
                    <h3 className="font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-faint">{t("blog:overview")}</h3>
                    <ul className="mt-3 flex flex-col gap-1">
                      {headings.map((h) => (
                        <li key={h.id}>
                          <a
                            href={`#${h.id}`}
                            onClick={() => setActiveId(h.id)}
                            aria-current={activeId === h.id ? "true" : undefined}
                            className={`block border-l-2 py-1 pl-3 text-[14px] leading-snug transition-colors ${
                              activeId === h.id
                                ? "border-primary font-medium text-ink"
                                : "border-line text-ink-soft hover:border-line-strong hover:text-ink"
                            }`}
                          >
                            {h.h}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <h3 className="font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-faint">{t("blog:sharePost")}</h3>
                  <div className="mt-3 flex items-center gap-2">
                    {[Linkedin, Facebook, Mail, Link2].map((Icon, i) => (
                      <span
                        key={i}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-ink-faint transition-colors hover:border-primary/40 hover:text-primary"
                      >
                        <Icon size={16} strokeWidth={1.8} />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* Related — "You may also like these" */}
      {related.length > 0 && (
        <section className="bg-bg">
          <Container className="py-20 md:py-24">
            <h2 className="text-center font-display text-[32px] font-semibold leading-[1.1] tracking-tight text-ink md:text-[40px]">
              {t("blog:related")}
            </h2>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <PostCard
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  tag={p.tag}
                  title={p.title}
                  read={p.read}
                  date={POST_DATE[p.slug]}
                  image={coverFor(p)}
                />
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* CTA */}
      <section className="bg-white">
        <Container className="py-20 md:py-28">
          <div className="grid items-stretch gap-3 rounded-3xl border border-line bg-white p-3 shadow-[0_28px_64px_-28px_rgba(28,25,23,0.22)] md:grid-cols-2">
            <div className="relative min-h-[240px] overflow-hidden rounded-2xl bg-bg-subtle">
              <img src={cmsImage(t, "blog:cta.image", PLACEHOLDER_PHOTO)} alt={t("blog:cta.imageAlt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-between gap-10 rounded-2xl bg-bg-subtle p-4 md:p-8">
              <h3 className="font-display text-[22px] font-semibold leading-[1.2] tracking-tight text-ink md:text-[24px]">
                {t("blog:cta.title")}
              </h3>
              <div className="flex flex-col gap-5">
                <p className="text-[15px] leading-relaxed text-ink-soft">
                  {t("blog:cta.body")}
                </p>
                <LocaleLink
                  to={TRIAL_PATH}
                  className="inline-flex w-fit items-center justify-center gap-2 rounded-lg bg-ink px-6 py-3 font-display text-[14px] font-semibold text-white transition-colors hover:bg-ink/90"
                >
                  {t("blog:cta.startTrial")} <ArrowRight size={15} strokeWidth={2} />
                </LocaleLink>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

